import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { TFunction } from "i18next";
import Image from "next/image";

type ProfileMenuProps = {
  name: string;
  picture: string;
  translate: TFunction;
  logout: () => Promise<void>;
  direction: string;
};

export const ProfileMenu = ({ name, picture, translate, logout, direction }: ProfileMenuProps) => {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (!menuRef.current?.contains(event.target as HTMLElement)) {
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navigateToProfile = () => {
    router.push("/profile").then();
    toggleMenu();
  };

  const _logout = async () => {
    try {
      localStorage.clear();
      await logout();
      window.location.href = "/";
    } catch (error) {
      console.error("Logout failed:", error);
      window.location.href = "/";
    }
  };

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        onClick={toggleMenu}
        type="button"
        className="inline-flex gap-4 items-center w-full rounded-md px-4 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <Image
          src={picture || "/images/avatar.png"}
          alt="Profile"
          width={48}
          height={48}
          className="w-12 h-12 rounded-full border-2 border-orange-300"
        />
        <span className="hidden lg:flex">{name}</span>
        <Image
          className="block h-3 w-auto rotate-90"
          src="/images/chevron.svg"
          alt="right icon svg"
          width={12}
          height={12}
        />
      </button>

      {isMenuOpen && (
        <div
          className={`origin-top-${direction === "ltr" ? "right" : "left"} absolute ${
            direction === "ltr" ? "right-0" : "left-0"
          } mt-2 w-[180px] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none`}
        >
          <div
            className="py-1"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            <div className="w-full flex items-center justify-between text-gray-700 hover:bg-gray-100 px-4 py-2">
              <button
                className="flex-1 flex justify-start text-sm md:text-base"
                role="menuitem"
                onClick={navigateToProfile}
              >
                {translate("profile")}
              </button>
              <Image
                className="h-3 w-auto rtl:rotate-180"
                src="/images/chevron.svg"
                alt="right icon svg"
                width={12}
                height={12}
              />
            </div>
            <button
              className="flex justify-start text-sm md:text-base w-full  items-center  text-gray-700 hover:bg-gray-100 px-4 py-2"
              role="menuitem"
              onClick={_logout}
            >
              {translate("logout")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
